import { ALL_WEEK_DAYS, Office } from "@gettactic/api";
import { getDefaultsHoursFromOffice } from "@gettactic/helpers";
import { calculateHoursDifference } from "@gettactic/helpers/src/date-time/calculateHoursDifference";
import { capTimeWindowToOpenHours } from "@gettactic/helpers/src/schedules/capTimeWindowToOpenHours";
import { TimeWindow } from "@gettactic/helpers/src/schedules/isWithinWorkingHours";
import { useCallback, useMemo } from "react";
import { useOperationalHours } from "../hours/useOperationalHours";

const WEEK_START_ON_MONDAY = Object.freeze([6, 0, 1, 2, 3, 4, 5]);

export function useDefaultOfficeHours(office: Office) {
  const { id: officeId } = office;
  const workingHours = useOperationalHours(
    officeId ? { id: officeId } : undefined
  );

  // Memoize to prevent creating new objects on every render
  const definedOfficeHour = useMemo(
    () => getDefaultsHoursFromOffice(office),
    [office?.id, office?.settings?.reservation_default_time]
  );

  // getDefaultsHoursFromOffice already calculates endHour and endMinutes
  const definedOfficeHourWindow: TimeWindow = useMemo(
    () => ({
      startHour: definedOfficeHour.startHour,
      startMinutes: definedOfficeHour.startMinutes,
      endHour: definedOfficeHour.endHour,
      endMinutes: definedOfficeHour.endMinutes
    }),
    [definedOfficeHour]
  );

  return useCallback(
    (forDay: Date) => {
      if (!workingHours.isSuccess) {
        return undefined;
      }
      if (workingHours.isFetched && workingHours.data?.elements?.length === 0) {
        return definedOfficeHourWindow;
      }

      const operational = workingHours.data?.elements ?? [];
      const dayOfWeekName =
        ALL_WEEK_DAYS[WEEK_START_ON_MONDAY[forDay.getDay()]];
      const currentWorkingHour = operational.find((x) =>
        x.days?.find((xx) => xx.toLowerCase() === dayOfWeekName)
      );
      const workingHoursOpenWindow = currentWorkingHour
        ? calculateHoursDifference(
            currentWorkingHour.start_time,
            currentWorkingHour.end_time
          )
        : 0;
      const isOpen24 = workingHoursOpenWindow === 24;

      if (!currentWorkingHour) {
        return definedOfficeHourWindow;
      }

      if (isOpen24) {
        return definedOfficeHourWindow;
      }

      const [openHour, openMinutes] = currentWorkingHour.start_time.split(":");
      const [closeHour, closeMinutes] = currentWorkingHour.end_time.split(":");

      // The reservation default times decide the window; building hours only
      // cap it, so it never starts before opening or ends after closing.
      return capTimeWindowToOpenHours(definedOfficeHourWindow, {
        startHour: Number.parseInt(openHour, 10),
        startMinutes: Number.parseInt(openMinutes, 10),
        endHour: Number.parseInt(closeHour, 10),
        endMinutes: Number.parseInt(closeMinutes, 10)
      });
    },
    [
      workingHours.isSuccess,
      officeId,
      definedOfficeHour,
      definedOfficeHourWindow
    ]
  );
}

/**
 * Sets end time on a date object, handling day overflow when end time wraps to next day.
 * If end time is before start time, adds one day to handle overflow.
 */
export const setEndTimeWithDayOverflow = (
  endDate: Date,
  startDate: Date,
  endHour: number,
  endMinutes: number
) => {
  endDate.setHours(endHour, endMinutes, 0);
  // If end time is before or equal to start time, it means it wrapped to the next day
  // Add one day to handle day overflow
  if (endDate.getTime() <= startDate.getTime()) {
    endDate.setDate(endDate.getDate() + 1);
  }
};
